import { useCallback, useMemo } from "react";
import { skipToken } from "@tanstack/react-query";

import { isProductOfType } from "../../../../commons/libs/products";
import { getSingleSpecValue, getStandaloneProductVariants } from "../../../../commons/libs/specs";
import { getWawiProductAvailabilty, WawiError } from "../../../../commons/libs/wawi-service";
import { Product, ProductId, ProductKey, ProductSpecKey, ProductType } from "../../../../commons/specs/product";
import { WawiProductAvailabilityVariant, type GetWawiProductAvailabiltyResponse } from "../../../../commons/types/wawi";
import useQuery from "../hooks/use-query";

import { useBrands } from "./use-brands";
import { useWawiConnectionInfo } from "./use-wawi-connection";

/** A hook that fetches the WAWI Product Availabilities for a given product and provides utility functions to interact with it. */
const useWawiProductAvailabilities = (product: Product) => {
  const wawiConnectionInfo = useWawiConnectionInfo();
  const brands = useBrands();

  // TODO: BCD-6796 Enable wawi for all products
  const isBicycle = isProductOfType(product, ProductType.Bicycle);
  const brandKey = getSingleSpecValue(ProductSpecKey.BrandKey, product);
  const variants = useMemo(() => getStandaloneProductVariants(product), [product]);

  const isAvailabilityActive = brands.getBrand(brandKey)?.availabilityPossible ?? false;
  const isActive = isBicycle && isAvailabilityActive && wawiConnectionInfo.isWawiConfigured;

  const query = useQuery<GetWawiProductAvailabiltyResponse>({
    queryKey: ["wawi-product-availability", product[ProductKey.ProductId], product] as const,
    queryFn: isActive ? () => getWawiProductAvailabilty(product) : skipToken
  });

  const error = query.error instanceof WawiError ? query.error.wawiError : undefined;

  const availabilities = query.data?.variants;

  const getAvailabilityByVariant = useCallback(
    (variantId: ProductId): WawiProductAvailabilityVariant | undefined =>
      (availabilities ?? []).find(variant => variant.variantId === variantId),
    [availabilities]
  );

  const hasAtLeastOneValidVariant = useMemo(
    () =>
      variants.some(variant => {
        const wawiDetails = getAvailabilityByVariant(variant[ProductKey.VariantId]);
        return wawiDetails && !wawiDetails.itemUnknown;
      }),
    [variants, getAvailabilityByVariant]
  );

  return {
    query,
    error,
    isActive,
    getAvailabilityByVariant,
    hasAtLeastOneValidVariant
  };
};

export type UseWawiProductAvailabilitiesReturn = ReturnType<typeof useWawiProductAvailabilities>;

export default useWawiProductAvailabilities;
